import React from 'react';
import { useTheme } from '../../hooks/useTheme.jsx';
import { Moon, Sun, Bell, Lock, Globe, Smartphone, Monitor, EyeOff } from 'lucide-react';
import ThemeToggle from '../common/ThemeToggle.jsx';
import PushToggle from '../common/PushToggle.jsx';
import EmailCategoryPrefs from '../common/EmailCategoryPrefs.jsx';
import { useTranslation } from 'react-i18next'
import LanguagePicker from '../common/LanguagePicker.jsx'
import StagingMailToggle from '../common/StagingMailToggle.jsx'
import { usePrivacy, PRIVACY_KEY } from '../../hooks/usePrivacy.jsx'

const Preferences = () => {
  const { t } = useTranslation('people')
    const { isDark } = useTheme();
    const { enabled: privacyOn, setPrivacy, badgeVisible, setBadgeVisible } = usePrivacy();

    // Dynamic Styles
    const cardClass = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
    const textPrimary = isDark ? 'text-white' : 'text-gray-900';
    const textSecondary = isDark ? 'text-gray-400' : 'text-gray-500';
    const sectionheader = `text-lg font-semibold mb-4 ${textPrimary}`;


    return (
        <div className={`min-h-full p-6 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className="max-w-3xl mx-auto space-y-6">

                {/* header */}
                <div>
                    <h1 className={`text-2xl font-bold ${textPrimary}`}>{t('preferences')}</h1>
                    <p className={`mt-1 text-sm ${textSecondary}`}>{t('manage_experience')}</p>
                </div>

                {/* Appearance Section */}
                <div className={`p-6 rounded-2xl border shadow-sm ${cardClass}`}>
                    <h2 className={sectionheader}>{t('appearance')}</h2>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700 text-blue-400' : 'bg-blue-50 text-blue-600'}`}>
                                {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                            </div>
                            <div>
                                <p className={`font-medium ${textPrimary}`}>{t('theme_mode')}</p>
                                <p className={`text-sm ${textSecondary}`}>
                                    {isDark ? 'Currently using Dark mode' : 'Currently using Light mode'}
                                </p>
                            </div>
                        </div>
                        {/* Reusing existing ThemeToggle logic but maybe styled differently? 
                For now, let's just use the ThemeToggle component directly or a variation.
            */}
                        <div className="scale-110 origin-right">
                            <ThemeToggle />
                        </div>
                    </div>
                </div>

                {/* Screen sharing. The state here is the same one the floating control on
                    every page writes, so whichever one they use last is what they get next time.
                    Per browser, not per account: this device is the one being shared. */}
                <div className={`p-6 rounded-2xl border shadow-sm ${cardClass}`}>
                    <h2 className={sectionheader}>Screen sharing</h2>
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                                <EyeOff className="w-5 h-5" />
                            </div>
                            <div>
                                <p className={`font-medium ${textPrimary}`}>Hide personal details</p>
                                <p className={`text-sm ${textSecondary}`}>
                                    Blurs your address, phone, ID and bank details so they do not show if you
                                    share your screen. You can still see which field you are in and how much you
                                    have typed. It hides them from you as well, because your screen and the shared
                                    screen are the same pixels — press {PRIVACY_KEY} to make them readable when you need
                                    to check something, and again to blur them.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={privacyOn}
                            onClick={() => setPrivacy(!privacyOn, { persist: true })}
                            className={`relative shrink-0 w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${privacyOn ? 'bg-teal-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${privacyOn ? 'translate-x-6' : ''}`} />
                        </button>
                    </div>

                    <div className={`flex items-center justify-between gap-4 mt-5 pt-5 border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                        <div>
                            <p className={`font-medium ${textPrimary}`}>Show the badge on screen</p>
                            <p className={`text-sm ${textSecondary}`}>
                                The badge is drawn on the page, so people watching your screen can see it like
                                anything else. Turn it off if you would rather they saw no sign of this at all —
                                {PRIVACY_KEY} still works, and is the only way in once the badge is gone.
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={badgeVisible}
                            onClick={() => setBadgeVisible(!badgeVisible)}
                            className={`relative shrink-0 w-12 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${badgeVisible ? 'bg-teal-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}
                        >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${badgeVisible ? 'translate-x-6' : ''}`} />
                        </button>
                    </div>
                </div>


                {/* Notifications Section */}
                <div className={`p-6 rounded-2xl border shadow-sm ${cardClass}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <Bell className="w-5 h-5 text-blue-500" />
                        <h2 className={`text-lg font-semibold ${textPrimary}`}>{t('notifications')}</h2>
                    </div>
                    <div className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-100'}`}>

                        {/* Real per-device push toggle (moved here from Profile Settings) */}
                        <div className="py-4">
                            <PushToggle variant="settings" />
                        </div>
                        {/* Replaces "Email Notifications" and "Project Updates", which moved
                            React state and nothing else — no request, no column. Someone could
                            switch email off, keep receiving it, and rightly conclude the app
                            lies. "Project Updates" was worse: there is no following a project
                            to be notified about. */}
                        <EmailCategoryPrefs isDark={isDark} />
                    </div>
                </div>

                <StagingMailToggle isDark={isDark} />

            {/* Regional Settings */}
                <div className={`p-6 rounded-2xl border shadow-sm ${cardClass}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <Globe className="w-5 h-5 text-green-500" />
                        <h2 className={`text-lg font-semibold ${textPrimary}`}>{t('regional_settings')}</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('language')}</label>
                            <LanguagePicker />
                        </div>
                        <div>
                            <label className={`block text-sm font-medium mb-2 ${textSecondary}`}>{t('timezone')}</label>
                            {/* Read-only on purpose. This was a <select> with no value and no
                                onChange, offering four arbitrary zones — choosing one did
                                nothing. ProTrack has no stored timezone preference, so the
                                honest thing is to state the zone actually being used. */}
                            <p className={`px-4 py-2.5 rounded-lg border ${isDark ? 'bg-gray-900 border-gray-700 text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`}>
                                {Intl.DateTimeFormat().resolvedOptions().timeZone}
                                <span className={`block text-xs mt-0.5 ${textSecondary}`}>{t('timezone_detected')}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Security Mockup */}
                <div className={`p-6 rounded-2xl border shadow-sm ${cardClass}`}>
                    <div className="flex items-center gap-2 mb-4">
                        <Lock className="w-5 h-5 text-purple-500" />
                        <h2 className={`text-lg font-semibold ${textPrimary}`}>{t('security')}</h2>
                    </div>
                    <div className="flex justify-between items-center">
                        <div>
                            <p className={`font-medium ${textPrimary}`}>{t('two_factor')}</p>
                            <p className={`text-sm ${textSecondary}`}>{t('extra_security')}</p>
                        </div>
                        <button className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                            Enable 2FA
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Preferences;
